import "./assets/styles/layers.css";
import { createApp } from "vue";
import { installQueryJobLookup } from "@/plugins/queryJobLookup";
import App from "./App.vue";
import { registerPlugins, revalidateSessionAndInstallRouter } from "@/plugins";
import dayjs from "dayjs";
import { vMaska } from "maska/vue";
import * as Sentry from "@sentry/vue";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isoWeek from "dayjs/plugin/isoWeek";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { ESubjects, PermActions } from "@/api/models/auth/ability";
import "@/utils/AppError";
import { hideAppSplash } from "@/utils/appSplash";
import { browserDiagnosticsEnabled, installBrowserDiagnostics } from "@/plugins/browserDiagnostics";

import "driver.js/dist/driver.css";
import "./style.css";
import "@/assets/styles/filterField.css";
import "@/assets/styles/components/compactView.scss";
import "./style-tour.css";
import "./assets/tailwind.css";
import "vue-cal/style.css";

import { register } from "swiper/element/bundle";
import { Chart, registerables } from "chart.js";
import { EEnvironmentType } from "@/stores/environmentStore";

import { plugin as VueTippy } from "vue-tippy";
import { tippyOptions } from "@/plugins/tippy";
import "tippy.js/dist/tippy.css";
import "tippy.js/animations/shift-away.css";
import "@/assets/styles/tippy-orbis.css";

if (import.meta.env.DEV) {
  await import("./dev-optimize");
}

register();
Chart.register(...registerables);

const app = createApp(App);

registerPlugins(app);

app.config.errorHandler = (err, _instance, info) => {
  console.error("[Vue errorHandler]", info, err);
};

if (import.meta.env.VITE_SENTRY_DSN && import.meta.env.MODE !== "test") {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    release: import.meta.env.VITE_SENTRY_RELEASE || "unknown",
    environment: import.meta.env.VITE_ENVIRONMENT || "unknown",
    enableLogs: browserDiagnosticsEnabled,
    sendDefaultPii: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        networkDetailAllowUrls: [
          globalThis.location.origin,
          ...(import.meta.env.VITE_ENVIRONMENT === EEnvironmentType.DEVEL
            ? [import.meta.env.VITE_API_BASE]
            : []),
        ],
        // Adding a bank account posts the full account number here; never record that body.
        networkDetailDenyUrls: [/\/billing\/payment-methods\/?(\?.*)?$/],
      }),
      Sentry.feedbackIntegration({
        autoInject: false,
        colorScheme: "light",
        themeLight: {
          submitBackground: "#6c5fc7",
          submitBackgroundHover: "#5a4eb3",
          submitBorder: "#6c5fc7",
          submitForeground: "#ffffff",
          accentBackground: "#6c5fc7",
          accentForeground: "#ffffff",
        },
        useSentryUser: {
          name: "username",
          email: "email",
        },
      }),
    ],
    tracesSampleRate: Number.isNaN(
      Number.parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE)
    )
      ? 1
      : Number.parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE),
    tracePropagationTargets: [new RegExp(import.meta.env.VITE_SENTRY_API_ORIGIN)],
    replaysSessionSampleRate: Number.isNaN(
      Number.parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE)
    )
      ? 1
      : Number.parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE),
    replaysOnErrorSampleRate: 1,
  });
}

installBrowserDiagnostics(app);

app.use(VueTippy, tippyOptions);

installQueryJobLookup();

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);
dayjs.extend(customParseFormat);

app.config.globalProperties.$dayjs = dayjs;
app.config.globalProperties.$permSubjects = ESubjects;
app.config.globalProperties.$permActions = PermActions;

app.directive("v-maska", vMaska);

// Rebuild identity + ability, THEN install the router. Installing it starts the
// first navigation, so it must come after /me — otherwise the guard evaluates
// privileges against a stale persisted snapshot.
await revalidateSessionAndInstallRouter(app);

app.mount("#app");

hideAppSplash();
